import { useState } from "react";

const talentOptions = ["歌唱", "舞蹈", "樂器演奏", "脫口秀 / 聊天", "遊戲實況", "美妝教學", "料理分享", "生活 Vlog", "其他才藝"];
const platformOptions = ["17LIVE", "浪LIVE", "其他平台", "尚未決定"];
const experienceOptions = ["完全零經驗", "有玩過直播但不熟", "有一些直播經驗", "有穩定直播經驗"];

export default function RecruitForm() {
  const [talents, setTalents] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const toggle = (arr: string[], setArr: (v: string[]) => void, val: string) => {
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append("name", (form.elements.namedItem("name") as HTMLInputElement).value);
    data.append("email", (form.elements.namedItem("email") as HTMLInputElement).value);
    data.append("phone", (form.elements.namedItem("phone") as HTMLInputElement).value);
    data.append("age", (form.elements.namedItem("age") as HTMLInputElement).value);
    data.append("experience", (form.elements.namedItem("experience") as HTMLSelectElement).value);
    data.append("talents", talents.join("、"));
    data.append("platforms", platforms.join("、"));
    data.append("intro", (form.elements.namedItem("intro") as HTMLTextAreaElement).value);
    data.append("ig_account", (form.elements.namedItem("ig_account") as HTMLInputElement).value);
    try {
      await fetch("https://readdy.ai/api/form/d7bvl3ulfkej60p1k98g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="recruit-form" className="py-20 bg-stone-50">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 rounded-full bg-red-50 text-red-600 text-xs font-semibold tracking-widest uppercase mb-4">
            Apply Now
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Noto Serif TC', serif" }}>
            直播主應徵報名
          </h2>
          <p className="text-gray-500 text-sm">填寫後我們會盡快聯繫你，預約面試機會！</p>
        </div>

        {submitted ? (
          <div className="bg-white rounded-3xl p-14 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-red-100 mx-auto mb-6">
              <i className="ri-star-fill text-4xl text-red-600" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Noto Serif TC', serif" }}>
              報名成功！明日之星就是你！
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              我們已收到你的應徵資料，將在近期透過 Email 或電話聯繫你安排面試。<br />
              也可以直接私訊我們的 Instagram 加速流程！
            </p>
            <a
              href="https://www.instagram.com/rappy.meme/"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-instagram-line" />
              私訊 Instagram
            </a>
          </div>
        ) : (
          <form
            data-readdy-form
            id="recruit-apply-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 md:p-10 flex flex-col gap-6"
          >
            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">姓名 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="你的姓名"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">聯絡電話 <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="0912-345-678"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">年齡</label>
                <input
                  type="number"
                  name="age"
                  min={18}
                  max={60}
                  placeholder="例：25"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
            </div>

            {/* IG Account */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Instagram 帳號</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                <input
                  type="text"
                  name="ig_account"
                  placeholder="your_ig_account"
                  className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors"
                />
              </div>
            </div>

            {/* Experience */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">直播經驗 <span className="text-red-500">*</span></label>
              <select
                name="experience"
                required
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors bg-white cursor-pointer"
              >
                <option value="">請選擇你的直播經驗</option>
                {experienceOptions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            {/* Talents */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                才藝 / 特長 <span className="text-gray-400 font-normal">（可複選）</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {talentOptions.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggle(talents, setTalents, t)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      talents.includes(t)
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-600"
                    }`}
                  >
                    {talents.includes(t) && <i className="ri-check-line mr-1" />}
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Platforms */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                有興趣的直播平台 <span className="text-gray-400 font-normal">（可複選）</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {platformOptions.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toggle(platforms, setPlatforms, p)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                      platforms.includes(p)
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-600"
                    }`}
                  >
                    {platforms.includes(p) && <i className="ri-check-line mr-1" />}
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Self Intro */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                自我介紹 / 為什麼想成為直播主
                <span className="text-gray-400 font-normal ml-2">（{charCount}/500）</span>
              </label>
              <textarea
                name="intro"
                rows={5}
                maxLength={500}
                placeholder="簡單介紹自己，說說你的才藝、個性，以及為什麼想加入我們..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <><i className="ri-loader-4-line animate-spin" /> 送出中...</>
              ) : (
                <><i className="ri-star-fill" /> 送出應徵報名</>
              )}
            </button>

            <p className="text-center text-gray-400 text-xs">
              也可以直接私訊 IG{" "}
              <a
                href="https://www.instagram.com/rappy.meme/"
                target="_blank"
                rel="nofollow noreferrer"
                className="text-red-500 hover:underline"
              >
                @rappy.meme
              </a>{" "}
              或在評論區留言「+1」
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
